/**
 * Shared types for the /users/me API.
 *
 * Single source of truth for the request/response contract between the
 * API (`packages/runtimes/api`) and its clients
 * (`packages/runtimes/web`).
 */

import type { Locale } from "@zplanem/translations"
import type { UserMainRole } from "./user-main-roles"

/**
 * The authenticated user as returned by the API. `language`, `aiContext`,
 * `firstName`, `lastName`, and `termsAcceptedAt` are nullable until the
 * relevant flow populates them. `createdAt` is always set; the wizard
 * uses it to choose between "welcome" and "just one more thing"
 * headings. `isOnboardingComplete` is computed server-side from the four
 * onboarding fields (firstName, lastName, language, termsAcceptedAt).
 */
export interface UserDTO {
	id: string
	email: string
	language: Locale | null
	aiContext: string | null
	firstName: string | null
	lastName: string | null
	/** User-selected avatar. Null means clients should fall back to auth photoUrl. */
	avatarUrl: string | null
	mainRole: UserMainRole
	termsAcceptedAt: string | null
	createdAt: string
	isOnboardingComplete: boolean
}

/**
 * Response body for `GET /api/common/users/me` and `PATCH /api/common/users/me`.
 */
export interface MeResponse {
	user: UserDTO
}

/**
 * Request body for `PATCH /api/common/users/me`. At least one field must
 * be present; all are independently optional.
 *
 * - Empty `aiContext` after trim is stored as null.
 * - `firstName` / `lastName` are trimmed; empty (post-trim) is rejected
 *   by Joi `min(1)`.
 * - `mainRole` is the user's primary professional role.
 * - `acceptTerms` is write-only: server records `new Date()` when true,
 *   `null` when false. Read-side observes acceptance via
 *   `UserDTO.termsAcceptedAt`.
 */
export interface UpdateMeBody {
	language?: Locale
	aiContext?: string
	firstName?: string
	lastName?: string
	avatarUrl?: string | null
	mainRole?: UserMainRole
	acceptTerms?: boolean
}

export const USER_LIMITS = {
	aiContextMax: 2000,
	firstNameMax: 60,
	lastNameMax: 60,
} as const
